import React, { useState, useEffect, useMemo, useCallback } from "react";
import _ from "lodash";
import ThemeableAgGrid from "../../common/components/agGrid/ThemeableAgGrid";
import Loading from "../../common/components/loading/loading";
import ReviewTradesToggle from "./reviewTradesToggle";
import { getProductWithQualityAttributes } from "../../common/services/products/productService";
import { filterProductQualityAttributeValues } from "../../common/services/products/productUtilities";
import { getActiveQualityAttributeValueIdsByMarket } from "../../common/services/markets/qualityAttributeValueMarketAssignmentService";
import { getMarketWithAttributes } from "../../common/services/markets/marketService";
import { useTranslation } from "react-i18next";

const ReviewTradesTable = (props) => {
  const { t } = useTranslation(["marketdata"]);
  const [state, setState] = useState({
    mappedTrades: [],
    market: {},
    product: null,
    isLoading: true,
  });

  const { trades, handleApprove, handleReject } = props;

  // Map trades to view model
  const mapToViewModel = useCallback((trades, market) => {
    if (!trades || !market) return [];

    const dateFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "numeric",
    };

    const dateTimeFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "numeric",
      hour: "numeric",
      hour12: false,
      minute: "numeric",
      second: "numeric",
    };

    return trades.map((trade) => {
      const marketAttributeIndex =
        market.marketAttributes &&
        market.marketAttributes.findIndex(
          (u) => u.priceBasisId === trade.priceBasisId
        );
      const currencySymbol =
        (marketAttributeIndex > -1 &&
          market.marketAttributes[marketAttributeIndex].currencySymbol) ??
        "$";
      const pricePrecision =
        (marketAttributeIndex > -1 &&
          market.marketAttributes[marketAttributeIndex].pricePrecision) ??
        "2";
      const volumePrecision =
        (marketAttributeIndex > -1 &&
          market.marketAttributes[marketAttributeIndex].volumePrecision) ??
        "0";

      return {
        tradeId: trade.tradeId,
        organizationName: trade.organizationName,
        tradeDateFormatted: new Date(trade.tradeDate).toLocaleDateString(
          "en-US",
          dateFormatOptions
        ),
        priceFormatted:
          currencySymbol + Number(trade.price).toFixed(pricePrecision),
        volumeFormatted: Number(trade.volume).toFixed(volumePrecision),
        priceBasisName: trade.priceBasisName,
        termName: trade.termName,
        price: trade.price,
        volume: trade.volume,
        buyerOrganizationName: trade.buyerOrganizationName,
        sellerOrganizationName: trade.sellerOrganizationName,
        updatedDate: new Date(trade.updatedDate).toLocaleDateString(
          "en-US",
          dateTimeFormatOptions
        ),
        tradeState: trade.tradeState,
        ...trade.qualityAttributeValues.reduce((acc, attribute) => {
          acc[attribute.qualityAttributeId] = attribute.value;
          return acc;
        }, {}),
      };
    });
  }, []);

  // Fetch product and market data
  useEffect(() => {
    const fetchData = async () => {
      if (!trades || trades.length === 0) {
        setState((prev) => ({ ...prev, isLoading: false }));
        return;
      }

      const productId = trades[0].productId;
      const marketId = trades[0].marketId;
      const marketDate = new Date().toLocaleDateString("fr-CA");

      try {
        const [product, activeQualityAttributeValueIds, market] =
          await Promise.all([
            getProductWithQualityAttributes(productId),
            getActiveQualityAttributeValueIdsByMarket(marketId, marketDate),
            getMarketWithAttributes(marketId),
          ]);

        // Filter product's qualityAttributeValues to only include those with active market assignments
        const filteredProduct = filterProductQualityAttributeValues(
          product,
          activeQualityAttributeValueIds
        );

        const mappedTrades = mapToViewModel(trades, market);

        setState({
          mappedTrades: _.orderBy(mappedTrades, "updatedDate", "desc"),
          market,
          product: filteredProduct,
          isLoading: false,
        });
      } catch (error) {
        console.error("Error fetching data:", error);
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    };

    fetchData();
  }, [mapToViewModel, trades]);

  // Update mapped trades when trades change
  useEffect(() => {
    //if (state.market && trades !== props.trades) {
    const mappedTrades = mapToViewModel(trades, state.market);

    setState((prev) => ({
      ...prev,
      mappedTrades: _.orderBy(mappedTrades, "updatedDate", "desc"),
    }));
    //}
  }, [trades, state.market, mapToViewModel]);

  // Memoize column definitions
  const columnDefs = useMemo(() => {
    if (!state.product) return [];

    return [
      {
        field: "organizationName",
        headerName: t("SubmittingOrganizationName"),
        flex: 1,
        filter: false,
      },
      ...state.product.qualityAttributes
        .filter((qualityAttribute) => qualityAttribute.isPriceDriver !== false)
        .map((qualityAttribute) => ({
          field: qualityAttribute.qualityAttributeId,
          headerName: qualityAttribute.qualityAttributeName,
          flex: 1,
          filter: false,
        })),
      {
        field: "tradeDateFormatted",
        headerName: t("TradeDate"),
        flex: 1,
        filter: false,
      },
      {
        field: "priceBasisName",
        headerName: t("PriceBasis"),
        flex: 1,
        filter: false,
      },
      {
        field: "priceFormatted",
        headerName: t("Price"),
        flex: 1,
        filter: false,
      },
      {
        field: "volumeFormatted",
        headerName: t("Volume"),
        flex: 1,
        filter: false,
      },
      { field: "termName", headerName: t("Term"), flex: 1, filter: false },
      {
        field: "buyerOrganizationName",
        headerName: t("BuyerOrganization"),
        flex: 1,
        filter: false,
      },
      {
        field: "sellerOrganizationName",
        headerName: t("SellerOrganization"),
        flex: 1,
        filter: false,
      },
      {
        field: "updatedDate",
        headerName: t("UpdateDate"),
        flex: 1.2,
        filter: false,
      },
      {
        field: "tradeState",
        headerName: t("Action"),
        cellRenderer: (params) => {
          return (
            <ReviewTradesToggle
              tradeId={params.data.tradeId}
              tradeState={params.data.tradeState}
              handleApprove={handleApprove}
              handleReject={handleReject}
            />
          );
        },
        flex: 2.0,
        minWidth: 150,
        filter: false,
      },
    ];
  }, [state.product, t, handleApprove, handleReject]);

  // Memoize default column properties
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      type: "centerAligned",
      minWidth: 140,
      wrapText: false,
      autoHeight: false,
      wrapHeaderText: false,
      autoHeaderHeight: false,
    }),
    []
  );

  const autoSizeGridColumns = useCallback((params) => {
    params?.columnApi?.autoSizeAllColumns?.(false);
    params?.api?.autoSizeAllColumns?.(false);
  }, []);

  if (state.isLoading) return <Loading />;

  return (
    <div className="review-trades-table">
      <ThemeableAgGrid
        className="table-bold"
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        autoSizeStrategy={{
          type: "fitCellContents",
          skipHeader: false,
          defaultMinWidth: 140,
        }}
        rowData={state.mappedTrades}
        onFirstDataRendered={autoSizeGridColumns}
        onRowDataUpdated={autoSizeGridColumns}
        onGridSizeChanged={autoSizeGridColumns}
        getRowId={(params) => params.data.tradeId.toString()}
        animateRows={true}
        domLayout="autoHeight"
        allowHorizontalScroll={true}
      />
    </div>
  );
};

export default React.memo(ReviewTradesTable);
